import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Anchor, Lock } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@maritime.com");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Signed in successfully");
      navigate("/");
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Login failed";
      toast.error(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:col-span-3 relative bg-[#0a1d3f] text-[#f4ecd5] p-16 flex-col justify-between overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage:
            "repeating-linear-gradient(45deg, transparent 0 24px, rgba(212,175,55,0.4) 24px 25px)",
        }} />
        <div className="relative z-10 flex items-center gap-3">
          <Anchor className="h-7 w-7 text-[#d4af37]" />
          <span className="font-serif-display text-xl tracking-[0.25em]">
            FLAG·STATE REGISTRY
          </span>
        </div>

        <div className="relative z-10 max-w-xl">
          <h1 className="font-serif-display text-5xl xl:text-6xl leading-[1.1] mb-6">
            Endorsement
            <br />
            of Certificate
            <br />
            <span className="text-[#d4af37]">Issuance Console</span>
          </h1>
          <div className="brass-divider w-40 mb-6" />
          <p className="font-serif-body text-lg text-[#e6dcc0]/90 leading-relaxed">
            Issue STCW-compliant Certificates of Recognition for seafarers
            on behalf of registered maritime administrations. Each
            endorsement is sealed with a verifiable QR for global
            authentication.
          </p>
        </div>

        <div className="relative z-10 font-serif-body text-sm text-[#e6dcc0]/60">
          STCW 1978, as amended in 2010 · Regulation I/10
        </div>
      </div>

      {/* Right form panel */}
      <div className="lg:col-span-2 flex items-center justify-center p-8 parchment">
        <form
          onSubmit={onSubmit}
          className="w-full max-w-sm"
          data-testid="login-form"
        >
          <div className="lg:hidden flex items-center gap-2 mb-8 text-[#0a1d3f]">
            <Anchor className="h-6 w-6 text-[#b8860b]" />
            <span className="font-serif-display tracking-[0.25em] text-sm">
              FLAG·STATE
            </span>
          </div>

          <h2 className="font-serif-display text-3xl text-[#0a1d3f] mb-2">
            Administrator Sign-In
          </h2>
          <p className="font-serif-body text-stone-700 mb-8 text-sm">
            Authorised personnel only. Tokens valid for 12 hours.
          </p>

          <div className="space-y-5">
            <div>
              <Label
                htmlFor="email"
                className="font-serif-body text-stone-800 text-xs uppercase tracking-widest"
              >
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="mt-1 bg-white/70 border-stone-400 focus-visible:ring-[#0a1d3f]"
                data-testid="login-email-input"
              />
            </div>

            <div>
              <Label
                htmlFor="password"
                className="font-serif-body text-stone-800 text-xs uppercase tracking-widest"
              >
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="mt-1 bg-white/70 border-stone-400 focus-visible:ring-[#0a1d3f]"
                data-testid="login-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#0a1d3f] hover:bg-[#13295a] text-[#f4ecd5] font-serif-display tracking-widest h-11 rounded-none border border-[#d4af37]/40"
              data-testid="login-submit-btn"
            >
              <Lock className="h-4 w-4 mr-2" />
              {submitting ? "Authenticating…" : "Sign In"}
            </Button>
          </div>

          <p className="mt-6 text-xs text-stone-600 font-serif-body">
            Default: admin@maritime.com / Admin@123
          </p>
        </form>
      </div>
    </div>
  );
}
